<template>
  <div v-if="!idProf" class="text-muted text-center py-10">
    Selecione um profissional para ver a agenda.
  </div>
  <div v-else-if="loading" class="text-center py-10">
    <span class="spinner-border text-primary"></span>
  </div>
  <div
    v-else
    class="schedule-day-slots"
    :style="{ '--slot-h': slotPx + 'px' }"
  >
    <div class="schedule-day-slots__hours">
      <div
        v-for="(slot, index) in slots"
        v-show="slotHeights[index] > 0"
        :key="'h-' + slot.time"
        class="schedule-day-slots__hour"
        :class="{ 'is-now': slot.time === nowLabel, 'is-empty': !slot.occupied }"
        :style="{ height: slotHeights[index] + 'px' }"
      >
        {{ slot.time }}
      </div>
    </div>
    <div ref="canvas" class="schedule-day-slots__canvas" :style="{ height: canvasHeight + 'px' }">
      <div
        v-for="(slot, index) in slots"
        v-show="slotHeights[index] > 0"
        :key="'l-' + slot.time"
        class="schedule-day-slots__lane"
        :class="{
          'is-free': slot.free,
          'is-fitted': slot.fitted,
          'is-past': slot.past,
          'is-busy': slot.occupied,
          'is-now': slot.time === nowLabel,
          'is-group': isCollapsedGroupHead(index),
        }"
        :style="laneStyle(slot, index)"
        @click="onLaneClick(slot, index)"
      >
        <span v-if="isCollapsedGroupHead(index)" class="schedule-day-slots__group-label">
          <i class="bi bi-chevron-down"></i>
          {{ fittedGroupLabel(index) }}
        </span>
        <template v-else>
          <span v-if="slot.free">Horário livre · novo agendamento</span>
          <span v-else-if="slot.past">Já passou</span>
          <span v-else-if="slot.fitted">Sem disponibilidade · encaixe</span>
          <div
            v-if="isExpandedGroupHead(index) || canBlockSlot(slot, index)"
            class="schedule-day-slots__tools"
          >
            <button
              v-if="isExpandedGroupHead(index)"
              type="button"
              class="schedule-day-slots__retract"
              @click.stop="collapseFittedGroup(index)"
            >
              Recolher
            </button>
            <button
              v-if="canBlockSlot(slot, index)"
              type="button"
              class="schedule-day-slots__block"
              title="Bloquear horário"
              @click.stop.prevent="onBlockClick(slot)"
            >
              <i class="bi bi-lock-fill"></i>
            </button>
          </div>
        </template>
      </div>

      <div
        v-if="nowOffset != null"
        class="schedule-day-slots__now"
        :style="{ top: nowOffset + 'px' }"
      />

      <div
        v-for="block in blockedEvents"
        :key="block.key"
        class="schedule-day-slots__blocked"
        :style="block.style"
      >
        <div class="schedule-day-slots__blocked-main">
          <strong>Bloqueado</strong>
          <span v-if="block.label">{{ block.label }}</span>
          <small>{{ block.timeRange }}</small>
        </div>
        <button
          v-if="canUnblockSlot(block)"
          type="button"
          class="schedule-day-slots__unblock"
          title="Remover bloqueio"
          @click.stop.prevent="onUnblockClick(block)"
        >
          <i class="bi bi-unlock-fill"></i>
        </button>
      </div>

      <div
        v-for="event in timedEvents"
        :key="event.schedule.id_sched"
        class="schedule-day-slots__event"
        :class="{ 'is-compact': event.compact }"
        :data-sched-id="event.schedule.id_sched"
        :style="event.style"
      >
        <ScheduleAppointmentCard
          :schedule="event.schedule"
          :show-time="event.offGrid"
          @click="$emit('open-schedule', $event)"
          @takePicture="$emit('take-picture', $event)"
          @openRecurrence="$emit('open-recurrence', $event)"
          @onArriveSchedule="$emit('arrive', $event)"
          @onFinishSchedule="$emit('finish', $event)"
          @onReopenSchedule="$emit('reopen', $event)"
          @onStartAttendance="$emit('start', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ScheduleAppointmentCard from './ScheduleAppointmentCard.vue';
import {
  buildDaySlots,
  currentSlotLabel,
  isBlockedSchedule,
  isPatientSchedule,
  layoutDayTimelineBlocks,
  layoutDayTimelineEvents,
  resolveSlotStepMinutes,
  scheduleDurationMinutes,
  TIMELINE_EMPTY_SLOT_PX,
  TIMELINE_FITTED_GROUP_PX,
  fittedSlotGroups,
  timelineOffsetAt,
  timelineSlotHeight,
} from '../../../helpers/scheduleDay';
import moment from '../../../helpers/date';

const CARD_CONTENT_MAX_PX = 360;

export default {
  name: 'ScheduleDayTimeline',
  components: { ScheduleAppointmentCard },
  props: {
    schedules: { type: Array, default: () => [] },
    availabilitySlots: { type: Array, default: () => [] },
    dayRange: { type: Object, default: null },
    selectedDate: { type: String, required: true },
    professional: { type: Object, default: null },
    stepMinutes: { default: null },
    idProf: { default: null },
    loading: { type: Boolean, default: false },
    canBlock: { type: Boolean, default: false },
    canUnblock: { type: Boolean, default: false },
  },
  emits: ['open-schedule', 'take-picture', 'open-recurrence', 'arrive', 'finish', 'reopen', 'start', 'create-at', 'block-at', 'unblock-at'],
  data() {
    return {
      expandedFitted: {},
      contentHeights: {},
    };
  },
  computed: {
    slots() {
      return buildDaySlots(this.schedules, {
        date: this.selectedDate,
        begin: this.professional?.time_begin,
        end: this.professional?.time_end,
        stepMinutes: this.stepMinutes ?? this.professional?.interval_minutes,
        availabilitySlots: this.availabilitySlots,
        dayRange: this.dayRange,
      });
    },
    slotStep() {
      return resolveSlotStepMinutes(this.stepMinutes ?? this.professional?.interval_minutes, this.availabilitySlots, this.selectedDate);
    },
    slotPx() {
      return timelineSlotHeight(this.slotStep);
    },
    slotHeights() {
      return this.slots.map((slot, index) => {
        const starting = slot.starting || [];
        const continuing = slot.continuing || [];
        if (starting.concat(continuing).some((item) => isPatientSchedule(item) || isBlockedSchedule(item))) {
          const extra = starting.some((item) => isPatientSchedule(item)) ? this.startingExtra(slot) : 0;
          return this.slotPx + extra;
        }
        if (slot.occupied) return 0;
        const group = this.fittedGroupAt(index);
        if (group && !this.expandedFitted[group.start]) {
          return index === group.start ? TIMELINE_FITTED_GROUP_PX : 0;
        }
        return TIMELINE_EMPTY_SLOT_PX;
      });
    },
    fittedGroups() {
      return fittedSlotGroups(this.slots);
    },
    slotOffsets() {
      const offsets = [];
      let top = 0;
      this.slotHeights.forEach((height) => {
        offsets.push(top);
        top += height;
      });
      return offsets;
    },
    canvasHeight() {
      return this.slotHeights.reduce((sum, height) => sum + height, 0);
    },
    dayStart() {
      if (!this.slots.length) return null;
      return moment(this.slots[0].datetime);
    },
    nowLabel() {
      return currentSlotLabel(this.selectedDate, this.slotStep);
    },
    nowOffset() {
      if (!this.dayStart || this.selectedDate !== moment().format('YYYY-MM-DD')) return null;
      const minutes = moment().diff(this.dayStart, 'minutes', true);
      if (minutes < 0 || minutes > this.slots.length * this.slotStep) return null;
      return timelineOffsetAt(moment(), {
        dayStart: this.dayStart,
        step: this.slotStep,
        slotPx: this.slotPx,
        slotHeights: this.slotHeights,
      });
    },
    timelineScale() {
      return {
        dayStart: this.dayStart,
        step: this.slotStep,
        slotPx: this.slotPx,
        slotHeights: this.slotHeights,
      };
    },
    timedEvents() {
      if (!this.dayStart) return [];
      return layoutDayTimelineEvents(this.schedules, this.timelineScale).map((event) => {
        const time = event.schedule.sched_time || moment(event.schedule.schedule_date_time).format('HH:mm');
        return {
          ...event,
          offGrid: !this.slots.some((slot) => slot.time === time),
        };
      });
    },
    blockedEvents() {
      if (!this.dayStart) return [];
      return layoutDayTimelineBlocks(this.schedules, this.timelineScale).map((block, index) => ({
        ...block,
        key: block.item.schedule_date_time || `block-${index}`,
      }));
    },
  },
  watch: {
    loading(value) {
      if (!value) this.queueMeasure();
    },
    idProf() {
      this.expandedFitted = {};
      this.queueMeasure();
    },
    selectedDate() {
      this.expandedFitted = {};
      this.queueMeasure();
    },
  },
  mounted() {
    this.queueMeasure();
  },
  updated() {
    this.queueMeasure();
  },
  beforeUnmount() {
    if (this.measureFrame) cancelAnimationFrame(this.measureFrame);
  },
  methods: {
    queueMeasure() {
      if (this.measureFrame) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = 0;
        this.measureCards();
      });
    },
    startingExtra(slot) {
      const step = this.slotStep;
      let extra = 0;
      (slot.starting || []).filter((item) => isPatientSchedule(item)).forEach((schedule) => {
        const need = Number(this.contentHeights[schedule.id_sched]) || 0;
        if (need < 48) return;
        const minutes = Math.max(scheduleDurationMinutes(schedule, step), 1);
        const slotFraction = minutes / step;
        const natural = slotFraction * this.slotPx - 2;
        extra = Math.max(extra, Math.ceil((need - natural) / Math.min(slotFraction, 1)));
      });
      return Math.max(0, extra);
    },
    measureCards() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      const next = { ...this.contentHeights };
      let changed = false;
      canvas.querySelectorAll('.schedule-day-slots__event').forEach((wrap) => {
        const id = wrap.dataset.schedId;
        const card = wrap.querySelector('.sw-appt');
        const body = card && card.querySelector('.sw-appt__body');
        if (!id || !card || !body) return;
        const style = window.getComputedStyle(card);
        const pad = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
        const border = (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
        const inner = card.clientHeight - pad;
        if (body.scrollHeight <= inner + 1) return;
        const content = Math.ceil(body.scrollHeight + pad + border);
        if (content < 48 || content > CARD_CONTENT_MAX_PX || next[id] === content) return;
        next[id] = content;
        changed = true;
      });
      if (changed) this.contentHeights = next;
    },
    laneStyle(slot, index) {
      const gap = slot.occupied ? 0 : 2;
      return {
        top: `${this.slotOffsets[index] + gap}px`,
        height: `${this.slotHeights[index] - gap}px`,
      };
    },
    fittedGroupAt(index) {
      return this.fittedGroups.find((group) => index >= group.start && index <= group.end) || null;
    },
    isCollapsedGroupHead(index) {
      const group = this.fittedGroupAt(index);
      return Boolean(group && index === group.start && !this.expandedFitted[group.start]);
    },
    isExpandedGroupHead(index) {
      const group = this.fittedGroupAt(index);
      return Boolean(group && index === group.start && this.expandedFitted[group.start]);
    },
    fittedGroupLabel(index) {
      const group = this.fittedGroupAt(index);
      if (!group) return '';
      const first = this.slots[group.start];
      const last = this.slots[group.end];
      const count = group.end - group.start + 1;
      return `${first.time} – ${last.time} · ${count} horários sem disponibilidade`;
    },
    canBlockSlot(slot, index) {
      if (!this.canBlock || !this.idProf) return false;
      if (this.isCollapsedGroupHead(index) || slot.occupied || slot.past) return false;
      return Boolean(slot.free || slot.fitted);
    },
    canUnblockSlot(block) {
      return Boolean(this.canUnblock && this.idProf && block?.blockIds?.length);
    },
    onUnblockClick(block) {
      this.$emit('unblock-at', {
        ids: block.blockIds || [],
        label: [block.timeRange, block.label].filter(Boolean).join(' · '),
      });
    },
    onBlockClick(slot) {
      this.$emit('block-at', { datetime: slot.datetime, step: this.slotStep });
    },
    onLaneClick(slot, index) {
      if (this.isCollapsedGroupHead(index)) {
        this.expandFittedGroup(index);
        return;
      }
      if (!slot.occupied) this.$emit('create-at', slot.datetime);
    },
    expandFittedGroup(index) {
      const group = this.fittedGroupAt(index);
      if (!group) return;
      this.expandedFitted = { ...this.expandedFitted, [group.start]: true };
    },
    collapseFittedGroup(index) {
      const group = this.fittedGroupAt(index);
      if (!group) return;
      const next = { ...this.expandedFitted };
      delete next[group.start];
      this.expandedFitted = next;
    },
  },
};
</script>

<style scoped>
.schedule-day-slots {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  column-gap: 0.55rem;
  align-items: start;
}
.schedule-day-slots__hours {
  min-height: 0;
}
.schedule-day-slots__hour {
  font-size: 0.82rem;
  font-weight: 700;
  color: #a1a5b7;
  padding-top: 0.2rem;
  line-height: 1;
  box-sizing: border-box;
  min-height: 0;
  overflow: hidden;
}
.schedule-day-slots__hour.is-empty {
  font-size: 0.78rem;
  padding-top: 0.45rem;
}
.schedule-day-slots__hour.is-now {
  color: #780f5a;
}
.schedule-day-slots__canvas {
  position: relative;
  min-width: 0;
}
.schedule-day-slots__lane {
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  box-sizing: border-box;
  font: inherit;
}
.schedule-day-slots__lane.is-free,
.schedule-day-slots__lane.is-fitted {
  width: 100%;
  min-height: 0;
  padding: 0 0.7rem;
  font-size: 0.86rem;
  line-height: 1.2;
  border-radius: 0.55rem;
  box-shadow: none;
  justify-content: space-between;
  gap: 0.5rem;
}
.schedule-day-slots__lane.is-free {
  background: #e8f8ee;
  border: 1px dashed #b7e4c7;
  color: #2d6a4f;
}
.schedule-day-slots__lane.is-free:hover {
  background: #d8f3e2;
  border-color: #95d5b2;
  color: #1b4332;
}
.schedule-day-slots__lane.is-fitted {
  background: #f5f8fa;
  border: 1px dashed #e4e6ef;
  color: #a1a5b7;
}
.schedule-day-slots__lane.is-fitted:hover {
  border-color: #7e8299;
  color: #5e6278;
}
.schedule-day-slots__lane.is-past {
  width: 100%;
  min-height: 0;
  padding: 0 0.7rem;
  font-size: 0.86rem;
  line-height: 1.2;
  border-radius: 0.55rem;
  background: transparent;
  border: 1px solid #eef0f4;
  color: #c4cada;
  box-shadow: none;
}
.schedule-day-slots__lane.is-past:hover {
  border-color: #e4e6ef;
  color: #a1a5b7;
}
.schedule-day-slots__lane.is-busy {
  background: transparent;
}
.schedule-day-slots__lane.is-group {
  font-weight: 700;
  color: #5e6278;
}
.schedule-day-slots__group-label {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
}
.schedule-day-slots__tools {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-left: auto;
  flex-shrink: 0;
  position: relative;
  z-index: 3;
}
.schedule-day-slots__retract {
  flex-shrink: 0;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #780f5a;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
}
.schedule-day-slots__block {
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.7rem;
  height: 1.7rem;
  margin: 0;
  padding: 0;
  border: 1px solid #780f5a;
  border-radius: 0.4rem;
  background: #fff;
  color: #780f5a;
  cursor: pointer;
  box-shadow: none;
}
.schedule-day-slots__block i {
  font-size: 0.85rem;
  line-height: 1;
}
.schedule-day-slots__block:hover,
.schedule-day-slots__block:focus {
  background: #780f5a;
  border-color: #780f5a;
  color: #fff;
}
.schedule-day-slots__now {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 4;
  height: 2px;
  background: #f1416c;
  pointer-events: none;
}
.schedule-day-slots__now::before {
  content: '';
  position: absolute;
  left: -5px;
  top: -4px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #f1416c;
}
.schedule-day-slots__blocked {
  position: absolute;
  z-index: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  background: #f6e8fb;
  color: #7b16b5;
  font-weight: 700;
  font-size: 0.8rem;
  border-radius: 0.55rem;
  padding: 0.5rem 0.7rem;
  box-shadow: inset 3px 0 0 #a020f0;
}
.schedule-day-slots__blocked-main {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.1rem;
  min-width: 0;
}
.schedule-day-slots__blocked span,
.schedule-day-slots__blocked small {
  font-weight: 600;
  color: #8b2fc9;
}
.schedule-day-slots__blocked small {
  font-size: 0.72rem;
}
.schedule-day-slots__unblock {
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.7rem;
  height: 1.7rem;
  margin: 0;
  padding: 0;
  border: 1px solid #780f5a;
  border-radius: 0.4rem;
  background: #fff;
  color: #780f5a;
  cursor: pointer;
  box-shadow: none;
  flex-shrink: 0;
}
.schedule-day-slots__unblock i {
  font-size: 0.85rem;
  line-height: 1;
}
.schedule-day-slots__unblock:hover,
.schedule-day-slots__unblock:focus {
  background: #780f5a;
  border-color: #780f5a;
  color: #fff;
}
.schedule-day-slots__event {
  position: absolute;
  z-index: 2;
  min-width: 0;
  overflow: hidden;
}
.schedule-day-slots__event :deep(.sw-appt) {
  height: 100%;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
}
.schedule-day-slots__event :deep(.sw-appt__body) {
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  min-width: 0;
  height: auto;
  overflow: visible;
}
.schedule-day-slots__event :deep(.sw-appt__actions) {
  margin-top: auto;
  padding-top: 0.2rem;
}
.schedule-day-slots__event.is-compact :deep(.sw-appt) {
  grid-template-columns: 1fr;
  padding: 0.28rem 0.45rem;
  gap: 0;
}
.schedule-day-slots__event.is-compact :deep(.sw-appt__photo),
.schedule-day-slots__event.is-compact :deep(.sw-appt__actions),
.schedule-day-slots__event.is-compact :deep(.sw-appt__line),
.schedule-day-slots__event.is-compact :deep(.sw-appt__clocks) {
  display: none;
}
.schedule-day-slots__event.is-compact :deep(.sw-appt__meta) {
  display: none;
}
</style>
